import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuthStore } from "./auth";

/**
 * Session pulse — the beacon that ends TripMuscle's telemetry blackout.
 *
 * Until 2026-08-02 this product reported NOTHING about who was using it. Mission
 * Control's per-user support console could show, for every other product, when
 * somebody was last seen and roughly how long they spent; for TripMuscle it
 * printed an explicit gap card, and the onboarding watcher logged the same
 * "no activity telemetry" line on every run for every person. This is the fix.
 *
 * WHAT IT SENDS — three event kinds, no payload beyond a path:
 *   session_start  once per tab, per visit
 *   heartbeat      every 60s WHILE THE TAB IS VISIBLE (the duration unit)
 *   route          on client-side navigation
 *
 * WHERE IT SENDS — this origin's own `/api/pulse`, which forwards to Mission
 * Control with a shared key held server-side. The identity comes from the JWT
 * on the server, so nothing here sends an email address and no client can claim
 * to be somebody else. See backend/src/routes/pulseRoutes.ts.
 *
 * VISIBLE-ONLY HEARTBEATS ARE THE POINT, not an optimisation. A backgrounded
 * tab left open overnight would otherwise report eight hours of "time in app"
 * for somebody who was asleep, and the number this feeds is only worth having
 * if it can be defended.
 *
 * IT USES RAW fetch, NOT THE SHARED axios CLIENT — deliberately. That client
 * has a 401 interceptor that logs the user out and redirects to "/". A
 * telemetry request must never be able to end somebody's session; a beacon that
 * can log you out is worse than no beacon.
 */

const INTERVAL_MS = 60_000; // must match the server's heartbeat interval
const ENDPOINT = "/api/pulse";

type PulseKind = "session_start" | "heartbeat" | "route";

/** Per tab, per visit. Not persisted — a session is a visit, and a key that
 *  survived a reload would merge two visits into one and undercount sessions. */
function newSessionKey(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

function post(kind: PulseKind, sessionKey: string, path: string, token: string) {
  try {
    void fetch(ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      // keepalive so an in-flight beacon survives the page being torn down.
      // navigator.sendBeacon would be the usual choice, but it cannot set the
      // Authorization header this route authenticates with, and moving the
      // token into the body to work around that would put a credential in a
      // request body for no gain.
      keepalive: true,
      body: JSON.stringify({ kind, session_key: sessionKey, path }),
    }).catch(() => {
      /* telemetry must never surface as an error in the product */
    });
  } catch {
    /* ditto — a throw here would be a beacon breaking a page */
  }
}

/**
 * Mount once, inside the router and above the routes. Starts when a token
 * appears and stops when it goes away, so a logged-out visitor emits nothing:
 * an anonymous beacon cannot be attributed to anybody and the ingest rejects it.
 */
export function usePulse(): void {
  const token = useAuthStore((s) => s.token);
  const location = useLocation();
  const sessionKeyRef = useRef<string | null>(null);
  const pathRef = useRef(location.pathname);

  // Keep the latest path in a ref so the heartbeat interval below does not need
  // to be torn down and rebuilt on every navigation — restarting it on each
  // route change would reset the 60s timer and, for anyone browsing actively,
  // mean a heartbeat almost never fires and the duration reads as zero.
  pathRef.current = location.pathname;

  useEffect(() => {
    if (!token) {
      sessionKeyRef.current = null;
      return;
    }
    const sessionKey = newSessionKey();
    sessionKeyRef.current = sessionKey;

    post("session_start", sessionKey, pathRef.current, token);

    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        post("heartbeat", sessionKey, pathRef.current, token);
      }
    }, INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [token]);

  // Route changes. The first render is covered by session_start above, so this
  // skips its own initial run rather than double-counting the landing path.
  const lastSent = useRef<string | null>(null);
  useEffect(() => {
    const key = sessionKeyRef.current;
    if (!token || !key) return;
    if (lastSent.current === null) {
      lastSent.current = location.pathname;
      return;
    }
    if (lastSent.current === location.pathname) return;
    lastSent.current = location.pathname;
    post("route", key, location.pathname, token);
  }, [location.pathname, token]);
}

/** Renders nothing. Exists so App.tsx can mount the hook inside the router. */
export function PulseBeacon(): null {
  usePulse();
  return null;
}
